import { useContext, useEffect, useState } from 'react'

import { Skeleton } from '@mui/material'
import _ from 'lodash'

import { AppContext } from '/providers/app-context'
import { PassContext } from "/components/editor/pass-context"
import { getTemplateData } from '/providers/templates-api'
import { getGoogleBackgroundColour, getGoogleDarkBackgroundColour, contrastColor } from '/components/editor/google/colour-tool.js'

import AppleFront from "/components/editor/apple/front"
import AppleBack from "/components/editor/apple/back"
import GoogleFront from "/components/editor/google/front"
import GoogleBack from "/components/editor/google/back"
import GoogleLegacyFront from "/components/editor/google-legacy/front"
import GoogleLegacyBack from "/components/editor/google-legacy/back"

const TemplatePreview = ({ template, pass, classes }) => {

  const { apiSource, apiKey, client, selectedClientId, user } = useContext(AppContext)
  const clientId = selectedClientId ?? client?.client_id

  const [templateData,setTemplateData] = useState(null)
  const [variables,setVariables] = useState(null)
  
  const [isManagedPassTemplate,setIsManagedPassTemplate] = useState(false)

  const templateId = pass?.template_id

  useEffect(() => {
    let active = true

    const loadTemplate = async () => {
      if (!templateId) return
      const result = await getTemplateData({ apiSource, apiKey, templateId })
      if (!active || result.error) return

      setTemplateData(result.template)
      setIsManagedPassTemplate(Boolean(result.template.managed_pass_template))
      setVariables(_.merge({}, result.template.variables, pass?.variables))
    }

    loadTemplate()
    return () => { active = false }
  }, [apiKey, apiSource, pass, templateId])
  
  if (!pass || !template || !templateData) return (
    <div className={`flex flex-row gap-5 preview justify-evenly mt-5 ${classes ?? ''}`}>
      <div className='flex flex-col gap-5'>
        <Skeleton variant="rectangular" width={375} height={500}/>
        <Skeleton variant="rectangular" width={375} height={500}/>
      </div>
      <div className='flex flex-col gap-5'>
        <Skeleton variant="rectangular" width={375} height={500}/>
        <Skeleton variant="rectangular" width={375} height={500}/>
      </div>
    </div>

  )

  const googleBackgroundColour = getGoogleBackgroundColour({
    googleClass: templateData.google_class,
    googleObject: templateData.google_object,
    variables,
    googlePassType: templateData.google_pass_type,
  }) ?? '#FFFFFF'
  const googleDarkBackgroundColour = getGoogleDarkBackgroundColour({ googleBackgroundColour })
  const googleFgColour = contrastColor({ bgColor: googleBackgroundColour })
  const googleRenderer = templateData.google_pass_type === 'transit'
    ? 'legacy'
    : user?.preferences?.google_renderer ?? 'google'
  const googleStyles = {
    background: `linear-gradient(
      to bottom,
      ${googleBackgroundColour} 0px,
      ${googleBackgroundColour} 350px,
      ${googleDarkBackgroundColour} 750px,
      ${googleDarkBackgroundColour} 100%
    )`
  }

  return (
    <div className={`flex flex-row items-start gap-5 preview justify-evenly ${classes ?? ''}`}>
      <div className='flex flex-col gap-5'>
        <div className="apple">
          <div className="wrapper">
            <div className="pass">
              <PassContext.Provider value={{ device: "apple", preview: true, clientId, templateId: template.template_id, smartixPassType: template.pass_type_id, showEditableFields: false, model: templateData.apple_model, template: templateData.apple_template, variables, passType: templateData.apple_pass_type, subType: templateData.apple_sub_type, isManagedPassTemplate }}>
                <AppleFront />
              </PassContext.Provider>
            </div>
          </div>
        </div>

        <div className="apple">
          <div className="wrapper">
            <div className="pass">
              <PassContext.Provider value={{ device: "apple", preview: true, clientId, templateId: template.template_id, smartixPassType: template.pass_type_id, showEditableFields: false, model: templateData.apple_model, template: templateData.apple_template, variables, passType: templateData.apple_pass_type, subType: templateData.apple_sub_type, isManagedPassTemplate }}>
                <AppleBack showStandardPassItems={false} />
              </PassContext.Provider>
            </div>
          </div>
        </div>
      </div>

      <div className='flex flex-col gap-5'>
        {googleRenderer === 'legacy' ? <>
          <div className="google-legacy">
            <div className="wrapper">
              <div className="pass" style={{ backgroundColor: googleBackgroundColour }}>
                <PassContext.Provider value={{ device: "google", preview: true, clientId, templateId: template.template_id, smartixPassType: template.pass_type_id, showEditableFields: false, classData: templateData.google_class, objectData: templateData.google_object, variables, passType: templateData.google_pass_type, subType: templateData.google_sub_type, isManagedPassTemplate }}>
                  <GoogleLegacyFront />
                </PassContext.Provider>
              </div>
            </div>
          </div>

          <div className="google-legacy">
            <div className="wrapper">
              <div className="pass" style={{ backgroundColor: googleBackgroundColour }}>
                <PassContext.Provider value={{ device: "google", preview: true, clientId, templateId: template.template_id, smartixPassType: template.pass_type_id, showEditableFields: false, classData: templateData.google_class, objectData: templateData.google_object, variables, passType: templateData.google_pass_type, subType: templateData.google_sub_type, isManagedPassTemplate }}>
                  <GoogleLegacyBack />
                </PassContext.Provider>
              </div>
            </div>
          </div>
        </> : <>
          <div className="google">
            <div className="wrapper">
              <div className="pass" id="googleFront" style={googleStyles}>
                <PassContext.Provider value={{ device: "google", preview: true, clientId, templateId: template.template_id, smartixPassType: template.pass_type_id, showEditableFields: false, classData: templateData.google_class, objectData: templateData.google_object, variables, passType: templateData.google_pass_type, subType: templateData.google_sub_type, isManagedPassTemplate }}>
                  <GoogleFront backgroundColour={googleBackgroundColour} googleDarkBackgroundColour={googleDarkBackgroundColour} fgColour={googleFgColour} />
                </PassContext.Provider>
              </div>
            </div>
          </div>

          <div className="google">
            <div className="wrapper">
              <div className="pass" style={{ backgroundColor: googleDarkBackgroundColour }}>
                <PassContext.Provider value={{ device: "google", preview: true, clientId, templateId: template.template_id, smartixPassType: template.pass_type_id, showEditableFields: false, classData: templateData.google_class, objectData: templateData.google_object, variables, passType: templateData.google_pass_type, subType: templateData.google_sub_type, isManagedPassTemplate }}>
                  <GoogleBack backgroundColour={googleDarkBackgroundColour} fgColour={googleFgColour} />
                </PassContext.Provider>
              </div>
            </div>
          </div>
        </>}
      </div>

    </div>
  )
}

export default TemplatePreview
